import { fetchWithRetry, responseError } from './apiError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CACHE_MS = 60000;
const STORAGE_KEY = 'skill_store_visible';

export interface SkillStoreVisibility {
  enabled: boolean;
}

type VisibilityListener = (enabled: boolean) => void;

const readHint = (): boolean | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const writeHint = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // Storage is optional: the flag is re-read from the API on every load anyway.
  }
};

let cached: { enabled: boolean; fetchedAt: number } | null = null;
let pending: Promise<SkillStoreVisibility> | null = null;
/** Last value seen on a previous visit, used only for the first paint. */
let hint: boolean | null = readHint();
const listeners = new Set<VisibilityListener>();

const readResponse = async (response: globalThis.Response): Promise<SkillStoreVisibility> => {
  if (!response.ok) throw await responseError(response);

  const data = await response.json();

  return { enabled: data?.enabled !== false };
};

/** Record a freshly resolved flag value and wake every open surface. */
export const publishSkillStoreVisibility = (enabled: boolean): void => {
  cached = { enabled, fetchedAt: Date.now() };
  hint = enabled;
  writeHint(enabled);
  listeners.forEach((listener) => listener(enabled));
};

const load = (): Promise<SkillStoreVisibility> => {
  const promise = fetchWithRetry(`${API_URL}/api/v1/skills/store-visibility`)
    .then(readResponse)
    .then((data) => {
      publishSkillStoreVisibility(data.enabled);

      return data;
    })
    .finally(() => {
      if (pending === promise) pending = null;
    });

  pending = promise;

  return promise;
};

/**
 * Read the deployment-wide Skill Store visibility flag.
 * The endpoint is public because the landing page renders before sign-in.
 */
const getSkillStoreVisibility = (): Promise<SkillStoreVisibility> => {
  if (cached && Date.now() - cached.fetchedAt < CACHE_MS) {
    return Promise.resolve({ enabled: cached.enabled });
  }

  return pending ?? load();
};

/**
 * Best known value without waiting on the API: this session's answer when one
 * exists, otherwise the previous visit's answer. Null on a first-ever visit.
 */
const peekSkillStoreVisibility = (): boolean | null => cached?.enabled ?? hint;

/** Subscribe to flag changes so open menus react to an admin toggle. */
const subscribeSkillStoreVisibility = (listener: VisibilityListener): (() => void) => {
  listeners.add(listener);

  return () => { listeners.delete(listener); };
};

export const skillStoreVisibilityService = {
  getSkillStoreVisibility,
  peekSkillStoreVisibility,
  subscribeSkillStoreVisibility,
};
